import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "../ui/button";

export default function Header({ logoUrl, actionBtnText, actionBtnUrl }) {
  const [isSticky, setIsSticky] = useState(false);
  const [mobileToggle, setMobileToggle] = useState(false);
  const { pathname } = useLocation();

  const isActive = (path) =>
    path === "/" ? pathname === "/" : pathname === path || pathname.startsWith(`${path}/`);

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileToggle) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileToggle]);

  useEffect(() => {
    setMobileToggle(false);
  }, [pathname]);

  return (
    <>
      <header
        className={`fixed left-1/2 -translate-x-1/2 z-50
  transition-all duration-500 ease-out
  ${
    isSticky
      ? "top-4 w-[92%] max-w-7xl rounded-2xl bg-white/85 backdrop-blur-xl border border-gray-200/70 shadow-[0_8px_30px_rgba(15,23,42,0.08)]"
      : "top-0 w-full bg-transparent border-transparent"
  }`}
      >
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 flex items-center justify-between h-[88px] sm:h-[90px] lg:h-[90px]">
        {/* Logo */}
        <Link to="/" className="flex items-center flex-shrink-0 min-w-0">
          <img
            src={logoUrl}
            alt="Algostine"
            className="h-[78px] sm:h-[84px] lg:h-[120px] w-auto max-w-[210px] sm:max-w-[230px] lg:max-w-none object-contain"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center">
          <ul className="flex items-center gap-1 list-none m-0 p-0">
            {[
              { to: "/", label: "Home" },
              { to: "/about", label: "About" },
              { to: "/service", label: "Services" },
              { to: "/blog", label: "Blog" },
              { to: "/portfolio", label: "Portfolio" },
              { to: "/contact", label: "Contact" },
            ].map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  aria-current={isActive(item.to) ? "page" : undefined}
                  className={`inline-flex items-center rounded-lg px-4 py-2 text-[17px] font-medium leading-relaxed transition-all duration-200 ${
                    isActive(item.to)
                      ? "font-semibold text-brand-teal"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <Link to={actionBtnUrl} className="hidden sm:inline-flex">
            <Button variant="brand" size="default">
              {actionBtnText}
            </Button>
          </Link>
          <button
            className={`lg:hidden flex flex-col justify-center gap-[5px] w-9 h-9 p-[6px] bg-white/90 sm:bg-transparent border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors duration-200 flex-shrink-0 ${
              mobileToggle ? "gap-0" : ""
            }`}
            onClick={() => setMobileToggle(!mobileToggle)}
            aria-label="Toggle menu"
            aria-expanded={mobileToggle}
          >
            <span
              className={`block w-full h-[2px] bg-gray-700 rounded-sm transition-all duration-300 ${
                mobileToggle ? "translate-y-[7px] rotate-45" : ""
              }`}
            />
            <span
              className={`block w-full h-[2px] bg-gray-700 rounded-sm transition-all duration-300 ${
                mobileToggle ? "opacity-0 scale-x-0" : ""
              }`}
            />
            <span
              className={`block w-full h-[2px] bg-gray-700 rounded-sm transition-all duration-300 ${
                mobileToggle ? "-translate-y-[7px] -rotate-45" : ""
              }`}
            />
          </button>
        </div>
        </div>
      </header>

      {/* Mobile Overlay - outside transformed header to avoid containing block bug */}
      {mobileToggle && (
        <div
          className="fixed inset-0 bg-black/40 z-[60] lg:hidden animate-[fadeIn_0.2s_ease]"
          onClick={() => setMobileToggle(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile Menu - outside header */}
      <div
        className={`fixed top-0 right-0 w-[300px] max-w-[85vw] h-[100dvh] h-screen bg-white z-[70] transform transition-transform duration-300 overflow-hidden flex flex-col shadow-[-4px_0_20px_rgba(0,0,0,0.1)] lg:hidden ${
          mobileToggle ? "translate-x-0" : "translate-x-full"
        }`}
        aria-hidden={!mobileToggle}
      >
        {/* Sidebar Header with Logo */}
        <div className="flex items-center justify-between px-6 h-[88px] border-b border-gray-100 flex-shrink-0">
          <Link to="/" onClick={() => setMobileToggle(false)} className="flex items-center min-w-0">
            <img src={logoUrl} alt="Algostine" className="h-[62px] w-auto max-w-[160px] object-contain" />
          </Link>
          <button
            onClick={() => setMobileToggle(false)}
            aria-label="Close menu"
            className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors flex-shrink-0"
          >
            <span className="text-[22px] leading-none text-gray-600">×</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <ul className="flex flex-col gap-0.5 list-none m-0 p-0">
          {[
            { to: "/", label: "Home" },
            { to: "/about", label: "About" },
            { to: "/service", label: "Services" },
            { to: "/blog", label: "Blog" },
            { to: "/portfolio", label: "Portfolio" },
            { to: "/contact", label: "Contact" },
          ].map((item) => (
            <li key={item.to}>
              <Link
                to={item.to}
                onClick={() => setMobileToggle(false)}
                aria-current={isActive(item.to) ? "page" : undefined}
                className={`flex rounded-lg px-4 py-3 text-[18px] transition-all duration-200 ${
                  isActive(item.to)
                    ? "font-semibold text-brand-teal"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
          <div className="mt-6 sm:hidden px-2">
            <Link to={actionBtnUrl} onClick={() => setMobileToggle(false)} className="block w-full">
              <Button variant="brand" size="default" className="w-full justify-center">
                {actionBtnText}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
